import AgentDomain from "entities/domain/agents/agent-domain";
import type ConversationDomain from "entities/domain/conversations/conversation-domain";
import InboxDomain, {
  ConversationScope,
  InboxAccessBits,
  InboxMyAccess,
  NO_INBOX_ACCESS,
} from "entities/domain/inbox";
import { ConversationTab } from "redux/features/conversations";
import { InboxScope } from "util/inbox-scope";
import { canManageTeamInbox, canManageUnassignedInbox } from "util/permissions";

/*
 * Per-Inbox conversation access (ENG-121, fuzey-api src/api/inbox/access.py).
 *
 * The server is authoritative: it filters every list and count and refuses
 * every action through the existing 403 path. This module only decides what
 * the screen offers, from each Inbox's `my_access.effective`, never from a
 * calculation on the stored grant scope.
 *
 * Every Inbox is open in this iteration: a grant widens what the role gives
 * and never restricts it.
 */

/**
 * What one person can be offered at one merchant: the merchant id, the
 * signed-in agent and the merchant's Inbox list as last read. A list kept
 * while it refetches stays in use (stale-while-revalidate).
 */
export interface InboxAccessContext {
  merchantId: number;
  agent: AgentDomain | null | undefined;
  inboxes: InboxDomain[] | undefined;
}

export const unionInboxAccess = (
  first: InboxAccessBits,
  second: InboxAccessBits
): InboxAccessBits => ({
  others: first.others || second.others,
  unassigned: first.unassigned || second.unassigned,
});

// The role's two merchant-wide conversation permissions as capabilities.
// Nothing at a merchant the agent does not belong to (the permission helpers
// assume membership).
export const roleInboxAccess = (
  merchantId: number,
  agent: AgentDomain | null | undefined
): InboxAccessBits =>
  agent?.merchants.some((merchant) => merchant.id === merchantId)
    ? {
        others: canManageTeamInbox(merchantId, agent),
        unassigned: canManageUnassignedInbox(merchantId, agent),
      }
    : NO_INBOX_ACCESS;

// The same on every Inbox, so the first Inbox that reports it answers.
const reportedInheritedAccess = (
  inboxes: InboxDomain[] | undefined
): InboxAccessBits | null =>
  inboxes?.find((inbox) => inbox.myAccess !== null)?.myAccess?.inherited ??
  null;

/**
 * What the role gives on every open Inbox of the merchant: the server's
 * `my_access.inherited`, or the role's permissions before the list is read.
 * Missing Inbox identity uses this and never widens it (fuzey-api
 * AgentAccess.effective(None)).
 */
export const inheritedInboxAccess = (
  context: InboxAccessContext
): InboxAccessBits =>
  reportedInheritedAccess(context.inboxes) ??
  roleInboxAccess(context.merchantId, context.agent);

/**
 * The capabilities in force in one Inbox. An Inbox that is not in the list
 * (archived, another merchant's, not read yet) or reports no access gets
 * what the role gives.
 */
export const inboxEffectiveAccess = (
  context: InboxAccessContext,
  inboxId: string | null
): InboxAccessBits => {
  const access =
    inboxId === null
      ? null
      : (context.inboxes?.find((inbox) => inbox.id === inboxId)?.myAccess ??
        null);

  return access?.effective ?? inheritedInboxAccess(context);
};

/**
 * The capabilities a scope offers. One Inbox offers its own. All Inboxes
 * offers a capability that holds in ANY Inbox: the server then lists, Inbox
 * by Inbox, only what each one allows.
 */
export const scopeInboxAccess = (
  context: InboxAccessContext,
  scope: InboxScope
): InboxAccessBits => {
  if (scope.kind === "inbox") {
    return inboxEffectiveAccess(context, scope.inboxId);
  }

  return (context.inboxes ?? []).reduce<InboxAccessBits>(
    (union, inbox) =>
      inbox.myAccess
        ? unionInboxAccess(union, inbox.myAccess.effective)
        : union,
    inheritedInboxAccess(context)
  );
};

/**
 * The capabilities for one conversation: those of its own Inbox, wherever it
 * is shown. Access in one Inbox never widens another. A conversation of
 * another merchant gets that merchant's role permissions, since its Inbox
 * list is not loaded here.
 */
export const conversationInboxAccess = (
  context: InboxAccessContext,
  conversation: ConversationDomain
): InboxAccessBits =>
  conversation.merchantId === context.merchantId
    ? inboxEffectiveAccess(context, conversation.inboxId)
    : roleInboxAccess(conversation.merchantId, context.agent);

/**
 * May a person holding `access` in a conversation's Inbox see and act on it?
 * Their own conversation always; an unassigned one with `unassigned`; a
 * colleague's with `others` (fuzey-api `may_access`).
 */
export const mayAccessConversation = (
  access: InboxAccessBits,
  agentId: number,
  ownerAgentId: number | null
): boolean => {
  if (ownerAgentId === null) {
    return access.unassigned;
  }

  return ownerAgentId === agentId || access.others;
};

// The ownership lens a tab shows, and the capability it needs.
export const isConversationTabAvailable = (
  tab: ConversationTab,
  access: InboxAccessBits
): boolean => {
  switch (tab) {
    case ConversationTab.Team:
      return access.others;
    case ConversationTab.Unassigned:
      return access.unassigned;
    default:
      return true;
  }
};

/*
 * Unread counts. Socket events carry the merchant's raw buckets; HTTP
 * carries them shaped for the person. These mirror fuzey-api
 * `adjusted_unread_counts` and `unread_count_per_inbox_to_dto`.
 */

export interface AdjustedUnreadCounts {
  personal: number;
  shared: number;
  unassigned: number;
}

/**
 * One scope's counts as a person holding `access` sees them. `shared` is
 * every unread conversation in the scope, `unassigned` the unassigned share
 * of it, `personal` the person's own.
 */
export const adjustedUnreadCounts = (
  access: InboxAccessBits,
  personal: number,
  shared: number,
  unassigned: number
): AdjustedUnreadCounts => {
  if (!access.others && !access.unassigned) {
    return { personal, shared: personal, unassigned: 0 };
  }

  if (access.others && !access.unassigned) {
    return {
      personal,
      shared: Math.max(shared - unassigned, 0),
      unassigned: 0,
    };
  }

  if (!access.others && access.unassigned) {
    return { personal, shared: personal + unassigned, unassigned };
  }

  return { personal, shared, unassigned };
};

// Each lens summed across the Inbox buckets, never the lenses together:
// personal and unassigned are already part of shared.
export const sumUnreadCounts = (
  buckets: AdjustedUnreadCounts[]
): AdjustedUnreadCounts =>
  buckets.reduce<AdjustedUnreadCounts>(
    (total, bucket) => ({
      personal: total.personal + bucket.personal,
      shared: total.shared + bucket.shared,
      unassigned: total.unassigned + bucket.unassigned,
    }),
    { personal: 0, shared: 0, unassigned: 0 }
  );

/*
 * Grant scopes and what they would add. Used for explanations only.
 */

const SCOPE_ACCESS: Record<ConversationScope, InboxAccessBits> = {
  own: NO_INBOX_ACCESS,
  own_and_unassigned: { others: false, unassigned: true },
  all: { others: true, unassigned: true },
};

export const conversationScopeAccess = (
  scope: ConversationScope | null
): InboxAccessBits => (scope === null ? NO_INBOX_ACCESS : SCOPE_ACCESS[scope]);

// Capabilities the stored grant would add that are not in effect now: the
// server is not enforcing scopes yet.
export const pendingGrantAccess = (access: InboxMyAccess): InboxAccessBits => {
  const granted = conversationScopeAccess(access.grantScope);

  return {
    others: granted.others && !access.effective.others,
    unassigned: granted.unassigned && !access.effective.unassigned,
  };
};

export const hasInboxAccess = (access: InboxAccessBits): boolean =>
  access.others || access.unassigned;

// Capabilities in effect that the role does not give: a grant adds them.
export const isAccessWidenedByGrant = (access: InboxMyAccess): boolean =>
  (access.effective.others && !access.inherited.others) ||
  (access.effective.unassigned && !access.inherited.unassigned);

export const isGrantPending = (access: InboxMyAccess): boolean =>
  hasInboxAccess(pendingGrantAccess(access));

// Where one person's access in an Inbox stands: extra access in effect,
// extra access saved but not applied yet, or the role alone.
export type InboxAccessState = "extra" | "pending" | "inherited";

export const inboxAccessState = (access: InboxMyAccess): InboxAccessState => {
  if (isGrantPending(access)) return "pending";
  return isAccessWidenedByGrant(access) ? "extra" : "inherited";
};

/**
 * Known when some stored scope would add access the server does not apply:
 * per-Inbox access is not enforced yet, so saving a scope does not activate
 * it. False when nothing shows it, which is not proof of enforcement.
 */
export const isScopeEnforcementKnownOff = (
  accesses: (InboxMyAccess | null)[]
): boolean =>
  accesses.some((access) => access !== null && isGrantPending(access));

// The Inboxes a scope covers that report the signed-in person's access.
export const inboxesWithReportedAccess = (
  inboxes: InboxDomain[],
  scope: InboxScope
): InboxDomain[] =>
  inboxes.filter(
    (inbox) =>
      inbox.myAccess !== null &&
      (scope.kind === "all" || inbox.id === scope.inboxId)
  );

// Several Inboxes at once: saved-but-inactive first, then extra access.
export const combinedInboxAccessState = (
  inboxes: InboxDomain[]
): InboxAccessState => {
  const states = inboxes.flatMap((inbox) =>
    inbox.myAccess ? [inboxAccessState(inbox.myAccess)] : []
  );

  if (states.includes("pending")) return "pending";
  return states.includes("extra") ? "extra" : "inherited";
};

// What a grant scope gives on top of a role: the access a person ends up
// with once it applies (open Inboxes widen only).
export const configuredInboxAccess = (
  inherited: InboxAccessBits,
  scope: ConversationScope | null
): InboxAccessBits =>
  unionInboxAccess(inherited, conversationScopeAccess(scope));

export const sameInboxAccess = (
  first: InboxAccessBits,
  second: InboxAccessBits
): boolean =>
  first.others === second.others && first.unassigned === second.unassigned;

export interface InboxAccessLevelOption {
  // What is stored when this level is chosen.
  scope: ConversationScope;
  // The access the person ends up with.
  access: InboxAccessBits;
  isRoleLevel: boolean;
}

/**
 * The access levels a person can be given in one Inbox, given their role:
 * the role's own level (stored as `own`), then each higher level a scope
 * reaches. Never a level below the role, and never two options that end in
 * the same access: the scope that names the level exactly is kept (choosing
 * "All conversations" stores `all`).
 */
export const inboxAccessLevelOptions = (
  inherited: InboxAccessBits
): InboxAccessLevelOption[] => {
  const options: InboxAccessLevelOption[] = [
    { scope: "own", access: inherited, isRoleLevel: true },
  ];

  for (const scope of ["own_and_unassigned", "all"] as ConversationScope[]) {
    const access = configuredInboxAccess(inherited, scope);
    const exact = sameInboxAccess(access, conversationScopeAccess(scope));
    const index = options.findIndex((option) =>
      sameInboxAccess(option.access, access)
    );

    if (index === -1) {
      options.push({ scope, access, isRoleLevel: false });
    } else if (exact && !options[index].isRoleLevel) {
      options[index] = { ...options[index], scope };
    }
  }

  return options;
};
